import { useCallback, useEffect, useState } from "react";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";

import {
  AboutUsData,
  AtAGlanceData,
  HeroData,
  PublicDevelopmentManagerDetailsPreviewParams,
  RichContentSectionData,
} from "../../../../../models";
import { getOrganisationListingPreview } from "../../../../../service/organisation";
import { Status } from "../../../../../service/Shared";

type CustomNavigateFunction = NavigateFunction;

interface UsePreviewReturnData {
  navigate: CustomNavigateFunction;
  previewUuid: string | undefined;
  dataIsLoading: boolean;
  previewData: DevelopmentManagerPreviewData | undefined;
}

export interface DevelopmentManagerPreviewData {
  hero: HeroData;
  atAGlance: AtAGlanceData;
  aboutUs: AboutUsData;
  ourServices: RichContentSectionData;
}

export const usePreview = (): UsePreviewReturnData => {
  const navigate = useNavigate();
  const { previewUuid } = useParams<PublicDevelopmentManagerDetailsPreviewParams>();

  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [previewData, setPreviewData] = useState<DevelopmentManagerPreviewData>();

  const fetchData = useCallback(async (): Promise<void> => {
    if (previewUuid) {
      const organisationPreviewRes = await getOrganisationListingPreview({
        previewUuid,
      });

      if (organisationPreviewRes.status === Status.Success && organisationPreviewRes.data) {
        setPreviewData({
          hero: {
            enabled: organisationPreviewRes.data.content?.hero?.enabled,
            title: organisationPreviewRes.data.content?.hero?.title,
            subtitle: organisationPreviewRes.data.content?.hero?.subtitle,
            imageUrl:
              organisationPreviewRes.data.listingFiles?.find(
                (file) => file.uuid === organisationPreviewRes.data?.content?.hero?.imageFileUuid
              )?.file?.url || null,
          },
          atAGlance: {
            enabled: organisationPreviewRes.data.content?.atAGlance?.enabled,
          },
          aboutUs: {
            enabled: organisationPreviewRes.data.content?.aboutUs?.enabled,
            content: organisationPreviewRes.data.content?.aboutUs?.content,
            imageUrl:
              organisationPreviewRes.data.listingFiles?.find(
                (file) => file.uuid === organisationPreviewRes.data?.content?.aboutUs?.imageFileUuid
              )?.file?.url || null,
          },
          ourServices: {
            enabled: organisationPreviewRes.data.content?.ourServices?.enabled,
            part1: {
              title: organisationPreviewRes.data.content?.ourServices?.part1?.title,
              content: organisationPreviewRes.data.content?.ourServices?.part1?.content,
              imageUrl:
                organisationPreviewRes.data.listingFiles?.find(
                  (file) => file.uuid === organisationPreviewRes.data?.content?.ourServices?.part1?.imageFileUuid
                )?.file?.url || null,
            },
            part2: {
              title: organisationPreviewRes.data.content?.ourServices?.part2?.title,
              content: organisationPreviewRes.data.content?.ourServices?.part2?.content,
              imageUrl:
                organisationPreviewRes.data.listingFiles?.find(
                  (file) => file.uuid === organisationPreviewRes.data?.content?.ourServices?.part2?.imageFileUuid
                )?.file?.url || null,
            },
            part3: {
              title: organisationPreviewRes.data.content?.ourServices?.part3?.title,
              content: organisationPreviewRes.data.content?.ourServices?.part3?.content,
              imageUrl:
                organisationPreviewRes.data.listingFiles?.find(
                  (file) => file.uuid === organisationPreviewRes.data?.content?.ourServices?.part3?.imageFileUuid
                )?.file?.url || null,
            },
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    fetchData().then(() => setDataIsLoading(false));
  }, [fetchData]);

  return {
    navigate,
    previewUuid,
    dataIsLoading,
    previewData,
  };
};
